import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Users, TrendingUp, Clock, Heart, Shield, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import AboutHeader from "./AboutHeader"
import Footer from './landing/Footer';
import Header from './landing/Header';
import SEOHeader from '../components/SEOHeader';


function AboutADHD() {
  const marineBlue = '#1e5f74'; // Dark marine blue color
  const marineBlueDark = '#164a5a'; // Even darker for hover states
  const lightBlue = '#5fb3d4'; 
  const navigate = useNavigate();

  const stats = [
    { icon: <Users className="w-8 h-8" />, number: "5-7%", text: "of children have ADHD", bgColor: "bg-blue-100", textColor: "text-blue-600" },
    { icon: <TrendingUp className="w-8 h-8" />, number: "2.5-5%", text: "of adults have ADHD", bgColor: "bg-green-100", textColor: "text-green-600" },
    { icon: <Clock className="w-8 h-8" />, number: "24-29", text: "months public wait time", bgColor: "bg-yellow-100", textColor: "text-yellow-600" },
    { icon: <Heart className="w-8 h-8" />, number: "Life-changing", text: "when properly treated", bgColor: "bg-red-100", textColor: "text-red-600" }
  ];

  const symptoms = [
    {
      title: "1. Inattention",
      items: [
        "Easily distracted",
        "Forgetful",
        "Struggles to follow instructions",
        "Disorganised"
      ]
    },
    {
      title: "2. Hyperactivity",
      items: [
        "Fidgets or squirms",
        "Talks a lot",
        "Always on the go"
      ]
    },
    {
      title: "3. Impulsivity",
      items: [
        "Interrupts others",
        "Can't wait for turns",
        "Acts without thinking"
      ]
    }
  ];

  const treatments = [
    {
      title: "Medication",
      description: "Stimulants (like Ritalin or Vyvanse) or non-stimulants"
    },
    {
      title: "Therapy",
      description: "Cognitive Behavioural Therapy (CBT), coaching, or counselling"
    },
    {
      title: "Lifestyle Adjustments",
      description: "Exercise, sleep routines, diet, and structure"
    },
    {
      title: "Education & Support",
      description: "Learning more about ADHD helps a lot"
    }
  ];

  return (
    
    <div className="min-h-screen bg-white">
      <SEOHeader
        title="About Us | ADHD Rapid Care"
        description="Learn about our clinicians and ADHD care model in Australia."
        path="/about-adhd"
      />
     <Header/>
      {/* Hero Section */}
      <section 
        className="text-white text-center py-20 px-4"
        style={{
          '--tw-gradient-from': '#155dfc',
          '--tw-gradient-to': '#009689',
          '--tw-gradient-stops': 'var(--tw-gradient-from), var(--tw-gradient-to)',
          backgroundImage: 'linear-gradient(to right, var(--tw-gradient-stops))'
        }}
      >
        <Brain className="w-16 h-16 mb-4 mx-auto text-blue-200" />
        <h1 className="text-4xl md:text-5xl font-bold mb-6">About ADHD</h1>
        <p className="max-w-3xl mx-auto text-lg text-blue-100">
          Your comprehensive guide to understanding Attention-Deficit/Hyperactivity Disorder. 
          Clear answers to your questions in simple, easy-to-understand language.
        </p>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-blue-600 mb-4">
              <Users className="w-12 h-12 mx-auto" />
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-2">5-7%</div>
            <div className="text-gray-600">of children have ADHD</div>
          </div>

          <div className="text-center">
            <div className="text-green-600 mb-4">
              <TrendingUp className="w-12 h-12 mx-auto" />
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-2">2.5-5%</div>
            <div className="text-gray-600">of adults have ADHD</div>
          </div>

          <div className="text-center">
            <div className="text-orange-600 mb-4">
              <Clock className="w-12 h-12 mx-auto" />
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-2">24-29</div>
            <div className="text-gray-600">months public wait time</div>
          </div>

          <div className="text-center">
            <div className="text-red-600 mb-4">
              <Heart className="w-12 h-12 mx-auto" />
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-2">Life-changing</div>
            <div className="text-gray-600">when properly treated</div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <Brain className="w-8 h-8 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">What is ADHD?</h2>
          </div>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            ADHD stands for Attention-Deficit/Hyperactivity Disorder. It's a neurodevelopmental condition that affects how people think, focus, 
            and behave. People with ADHD may struggle with attention, staying still, or controlling impulses.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded">
            <p className="text-blue-800">
              It's not a sign of laziness or bad parenting—it's a recognised medical condition.
            </p>
          </div>
        </div>

        {/* Is ADHD Real Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="text-green-600">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Is ADHD Real?</h2>
          </div>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong v>Yes, ADHD is real.</strong> It is recognised by major medical organisations like the World Health Organization and the Australian 
            Psychological Society. Brain scans and research studies show clear differences in how the brains of people with ADHD work.
          </p>
        </div>

        {/* What Are the Symptoms Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Are the Symptoms of ADHD?</h2>
          <p className="text-lg text-gray-700 mb-6">Symptoms usually fall into three categories:</p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Inattention */}
            <div className="bg-red-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-800 mb-4">1. Inattention</h3>
              <ul className="sspace-y-2 text-red-700">
                <li>• Easily distracted</li>
                <li>• Forgetful</li>
                <li>• Struggles to follow instructions</li>
                <li>• Disorganised</li>
              </ul>
            </div>

            {/* Hyperactivity */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-orange-800 mb-4">2. Hyperactivity</h3>
              <ul className="space-y-2 text-orange-700">
                <li>• Fidgets or squirms</li>
                <li>• Talks a lot</li>
                <li>• Always "on the go"</li>
              </ul>
            </div>

            {/* Impulsivity */}
            <div className="bg-yellow-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-yellow-800 mb-4">3. Impulsivity</h3>
              <ul className="space-y-2 text-yellow-700">
                <li>• Interrupts others</li>
                <li>• Can't wait for turns</li>
                <li>• Acts without thinking</li>
              </ul>
            </div>
          </div>

          <p className="mt-6 bg-gray-50 p-4 rounded">
            People may have one, two, or all three types of symptoms.
          </p>
        </div>
       
        {/* How Do You Get ADHD Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How Do You Get ADHD?</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            ADHD is usually inherited. That means it often runs in families. It's caused by differences in brain structure and function, especially in
            areas that manage attention, planning, and self-control.
          </p>

          <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded">
            <p className="text-red-800 font-semibold">
              It is NOT caused by watching too much TV, bad parenting, or eating sugar.
            </p>
          </div>
        </div>


        {/* How Do You Know If You Have It Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How Do You Know If You Have It?</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            You can't diagnose ADHD just by reading a list. You'll need a full assessment by a qualified professional, like a psychiatrist or clinical 
            psychologist. This includes interviews, questionnaires, and sometimes reports from parents, teachers, or partners.
          </p>

          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 rounded mb-6">
            <h3 className="text-xl font-bold text-yellow-800 mb-3">Can ADHD Be Misdiagnosed?</h3>
            <p className="text-yellow-800">
              Yes. ADHD shares symptoms with anxiety, depression, autism, learning disorders, and sleep problems. That's why a proper assessment by a 
              trained professional is essential.
            </p>
          </div>
        </div>

        {/* What Does ADHD Feel Like Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Does ADHD Feel Like on the Inside?</h2>
          
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="text-lg text-blue-800 leading-relaxed">
              ADHD can feel like constant noise in your head, racing thoughts, forgetfulness, emotional ups and downs, and a sense of being 
              overwhelmed. People often describe it as <strong>"knowing what to do but struggling to do it."</strong>
            </p>
          </div>
        </div>

        {/* Can ADHD Be Mild, Moderate, or Severe Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Can ADHD Be Mild, Moderate, or Severe?</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Yes. ADHD exists on a spectrum. Some people have mild symptoms that barely affect daily life. Others may have more severe 
            symptoms that interfere with work, school, and relationships.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Mild */}
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <h3 className="text-2xl font-bold text-green-800">Mild</h3>
              <p className="text-green-700">Barely affects daily life</p>
            </div>

            {/* Moderate */}
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <h3 className="text-2xl font-bold text-yellow-800">Moderate</h3>
              <p className="text-yellow-700">Some daily challenges</p>
            </div>

            {/* Severe */}
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <h3 className="text-2xl font-bold text-red-800">Severe</h3>
              <p className="text-red-700">Interferes with work/school</p>
            </div>
          </div>
        </div>

        {/* Does ADHD Affect Boys and Girls Differently Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Does ADHD Affect Boys and Girls Differently?</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Boys */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-xl font-bold text-blue-800 mb-3">Boys</h3>
              <p className="text-blue-700">More likely to show hyperactive behaviour</p>
            </div>

            {/* Girls */}
            <div className="bg-pink-50 rounded-lg p-4">
              <h3 className="text-xl font-bold text-pink-800 mb-3">Girls</h3>
              <p className="text-pink-700">Often show more quiet inattentiveness</p>
            </div>
          </div>

          <p className="text-gray-700">
            As a result, girls are often underdiagnosed or diagnosed later.
          </p>
        </div>

        {/* What is Executive Dysfunction Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What is Executive Dysfunction?</h2>
          
          <p className="text-gray-600">
            Executive dysfunction means trouble with organising, planning, prioritising, and following through. It's a core part of ADHD and 
            explains why people struggle with time management or task completion.
          </p>
        </div>

       

        {/* What Are the Treatments Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Are the Treatments?</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">Treatment usually includes a mix of the following:</p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Medication */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-xl font-bold text-blue-800 mb-3">Medication</h3>
              <p className="text-blue-700">Stimulants (like Ritalin or Vyvanse) or non-stimulants</p>
            </div>

            {/* Lifestyle Adjustments */}
            <div className="bg-orange-50 rounded-lg p-4">
              <h3 className="text-xl font-bold text-orange-800 mb-3">Lifestyle Adjustments</h3>
              <p className="text-orange-700">Exercise, sleep routines, diet, and structure</p>
            </div>

            {/* Therapy */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-xl font-bold text-green-800 mb-3">Therapy</h3>
              <p className="text-green-700">Cognitive Behavioural Therapy (CBT), coaching, or counselling</p>
            </div>

            {/* Education & Support */}
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="text-xl font-bold text-purple-800 mb-3">Education & Support</h3>
              <p className="text-purple-700">Learning more about ADHD helps a lot</p>
            </div>
          </div>

          <div className="mt-8 bg-green-50 border-l-4 border-green-600 p-6 rounded">
            <p className="text-green-800 font-semibold text-lg">
              There's no cure, but ADHD can be managed very effectively. Many people with ADHD thrive once they receive the right 
              treatment and support.
            </p>
          </div>
        </div>

        {/* Can I Be Assessed Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="text-red-600">
              <Clock className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Can I Be Assessed and Diagnosed Immediately?</h2>
          </div>

          {/* Warning Box */}
          <div className="bg-red-50 p-6 mb-6 border-l-4 border-red-600">
            <p className="text-red-800 font-semibold text-lg mb-4">
              Short answer: Unfortunately, no, especially if you rely on low-cost or public pathways in Australia.
            </p>
          </div>

          {/* Two Columns */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Left Column - Wait Times */}
            <div className='bg-gray-50 p-6 rounded-lg'>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Long Wait Times & Backlogs</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Public clinics: 12-29 months wait</li>
                <li>• WA public kids' services: 24-29 months</li>
                <li>• Private services: 6+ months to 1+ year</li>
                <li>• Many specialists not accepting new patients</li>
              </ul>
            </div>

            {/* Right Column - Costs */}
            <div className='bg-gray-50 p-6 rounded-lg'>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Costs Pile Up Quickly</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Private assessment: $800-$2,000+</li>
                <li>• Additional psychiatrist costs</li>
                <li>• Ongoing medication costs</li>
                <li>• Regional areas have worse shortages</li>
              </ul>
            </div>
          </div>

          {/* Solution Box */}
          <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded">
            <h3 className="text-xl font-bold text-blue-800 mb-3 "><span style={{color: lightBlue}}>ADHD</span>
            <span style={{color: marineBlue}}>RapidCare</span> Solution</h3>
            <p className="text-blue-800 font-semibold text-lg">
              In late 2025 ADHDRapidCare will be offering a solution to all these
costs and time issues with its advanced Assessment and Diagnosis 
service with a guaranteed turn-around of 14 days.
            </p>
            {/* <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold">
                Start Your Assessment
              </button>
              <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-semibold" onClick={() => navigate('/costs')}>
                View Costs & Rebates
              </button>
            </div> */}
          </div>
        </div>


        {/* What Happens After Diagnosis Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Happens After Being Diagnosed with ADHD?</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">Once you've been diagnosed with ADHD, here's what typically happens next:</p>

          <div className="space-y-4 mb-6">
            {/* Step 1 */}
            <div className="flex gap-4 bg-blue-50 p-4 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h3 className="font-bold text-blue-800">Discussion of Treatment Options</h3>
                <p className="text-blue-700">Review medication, therapy, and lifestyle approaches</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4 bg-green-50 p-4 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h3 className="font-bold text-green-800">Medication Trial (if chosen)</h3>
                <p className="text-green-700">Start with appropriate medication and monitor response</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4 bg-orange-50 p-4 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h3 className="font-bold text-orange-800">Supportive Tools and Planning</h3>
                <p className="text-orange-700">Develop strategies for daily management</p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4 bg-purple-50 p-4 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <div>
                <h3 className="font-bold text-purple-800">Ongoing Monitoring</h3>
                <p className="text-purple-700">Regular check-ins for legal, clinical, and safety reasons</p>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex gap-4 bg-green-50 p-4 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                5
              </div>
              <div>
                <h3 className="font-bold text-green-800">Long Term Management</h3>
                <p className="text-green-700">Adjustments as life changes and ongoing support</p>
              </div>
            </div>
          </div>

          {/* Info Boxes */}
          <div className="space-y-4">
            <div className="bg-blue-50 p-6 border-l-4 border-blue-600 mt-8 mb-6">
              <p className="text-blue-800">
                ADHD is ongoing—many people benefit from a combination of approaches. You don't just get diagnosed and sent on your way—good 
                care involves regular check-ins and adjustments as life changes.
              </p>
            </div>

            <div className="bg-orange-50 p-6 border-l-4 border-orange-600 mb-6">
              <p className="text-orange-800 font-semibold text-lg mb-3">
                Once patients are diagnosed with ADHD and begin treatment, ongoing monitoring is essential for legal, clinical, and safety 
                reasons.
              </p>
              <p className="text-orange-800">
                For ongoing ADHD Care, see the <a href="/care-plan" className="text-orange-600 underline hover:text-orange-800 font-semibold">ADHDRapidCare  Monthly Care 
Plan</a> now.
              </p>
            </div>
          </div>
        </div>

        {/* What Happens If ADHD Is Left Untreated Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="text-red-500">
              <AlertCircle className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Happens If ADHD Is Left Untreated?</h2>
          </div>

          <p className="text-lg text-gray-700 mb-6">Untreated ADHD can cause serious problems:</p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-red-700">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                Poor performance at school or work
              </li>
              <li className="flex items-center gap-2 text-red-700">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                Relationship breakdowns
              </li>
              <li className="flex items-center gap-2 text-red-700">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                Anxiety or depression
              </li>
            </ul>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-red-700">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                Low self-esteem
              </li>
              <li className="flex items-center gap-2 text-red-700">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                Risky or impulsive behaviours
              </li>
              
            </ul>
          </div>

          <div className="mt-6 bg-green-50 border-l-4 border-green-600 p-6 rounded">
            <p className="text-green-800 font-semibold">
              Early diagnosis and treatment can prevent these outcomes.
            </p>
          </div>
        </div>

        {/* Cost of Diagnosis Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What's the Cost of Diagnosis and Treatment in Australia?</h2>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Costs can vary depending on where you live and whether you use private or public services.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Diagnosis */}
            <div className="bg-red-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-800 mb-3">Diagnosis</h3>
              <p className="text-red-700">
                A full ADHD assessment with a psychiatrist or psychologist can range from $600 to $2500+
              </p>
            </div>

            {/* Medication */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-orange-800 mb-3">Medication</h3>
              <p className="text-orange-700">
                Costs depend on the type and whether it's subsidised under the PBS
              </p>
            </div>

            {/* Therapy & Support */}
            <div className="bg-yellow-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-yellow-800 mb-3">Therapy & Support</h3>
              <p className="text-yellow-700">
                Sessions with psychologists or coaches can range from $200 
to $600+ per hour
              </p>
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded mb-6">
            <p className="text-green-800 font-semibold text-lg">
              Note: ADHDRapidCare offers a full-service Care Plan for $79 per 
month. <span className="text-green-500 underline cursor-pointer" onClick={() => navigate('/care-plan')}>See here</span>
            </p>
          </div>

          <p className="text-gray-700">
           Some costs are covered by private health insurance.
          </p>
        </div>

        {/* Treatment Questions Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16">
          {/* Do Treatments Work */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Do Treatments Work?</h2>
            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded mb-6">
              <p className="text-green-800 font-semibold text-lg">
                Yes, treatment can be life-changing. Many people notice big improvements with the right mix of medication, therapy, and 
                support. It may take time to find the best approach, but the benefits are usually worth it.
              </p>
            </div>
          </div>

          {/* Are All Treatments Equally Effective */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Are All Treatments Equally Effective?</h3>
            <p className="text-gray-700 mb-4">
              Not always. Everyone is different. Some people respond well to medication, while others do better with therapy or lifestyle changes. The most 
              effective approach is usually a personalised combination of treatments.
            </p>
          </div>
        </div>

        {/* ADHD vs Autism Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-16 ">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Is ADHD a Form of Autism?</h2>
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded">
            <p className="text-blue-800 font-semibold text-lg">
              No, ADHD is not a form of autism. They are separate conditions, but they can sometimes appear together. Some people 
              have both ADHD and autism, which requires a slightly different approach to care.
            </p>
          </div>
        </div>

        {/* Ready to Get Started Section */}
        <div className="mt-16 rounded-2xl overflow-hidden ">
          <div className=" text-white py-12 px-8 text-center" style={{
            '--tw-gradient-from': '#155dfc',
            '--tw-gradient-to': '#009689',
            '--tw-gradient-stops': 'var(--tw-gradient-from), var(--tw-gradient-to)',
            backgroundImage: 'linear-gradient(to right, var(--tw-gradient-stops))'
          }}>
            <h2 className="text-3xl font-bold mb-4">Diagnosed with ADHD & want the best care?</h2>
            {/* <p className="text-xl text-blue-100 mb-8">
              Don't wait months for an assessment. Get your ADHD diagnosis in 14 days with ongoing support for life.
            </p>
            <div className="flex justify-center gap-4">
              <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Start Your Assessment
              </button>
              <button className="bg-green-600  text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors" onClick={() => navigate('/costs')}>
                View Costs & Rebates
              </button>
            </div> */}
            <p className="text-xl text-blue-100 mb-8">
              Plan Details
            </p>
             {/* Services and Pricing Section */}
            <div className="max-w-2xl mx-auto mb-6">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left Column - Services */}
                <div className="text-left">
                  <ul className="space-y-3 text-white">
                    <li className="flex items-start">
                      <span className="text-blue-200 mr-2">•</span>
                      <span>Weekly Smart SMS Monitoring</span>
                    </li>
                    <li className="flex items-start">
                        <span className="text-blue-200 mr-2">•</span>
                      <div>Weekly and monthly AI Reports</div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-200 mr-2">•</span>
                      <span>Monthly ADHD nurse meeting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-200 mr-2">•</span>
                      <span>Video or Phone</span>
                    </li>
                  </ul>
                </div>
                
                {/* Right Column - Pricing */}
                <div className="text-right flex items-center justify-center">
                  <div className="text-blue-200 font-semibold text-xl">Total Cost $79 per month</div>
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-4">
              
              <button className="bg-green-600  text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors" onClick={() => navigate('/care-plan')}>
                View details HERE
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer/>
    </div>
  );
}

export default AboutADHD; 